import type { ComponentChildren, VNode } from "preact";

// Minimal in-bubble markdown + UI-hint extraction. We deliberately avoid
// pulling in marked.js / micromark — adds ~50kb to a 40kb bundle. The model
// only emits a small subset of markdown in chat, so a handwritten parser
// covers the cases we care about:
//
//   **bold**            → <strong>
//   *italic*            → <em>
//   `code`              → <code>
//   line breaks         → <br/>
//
// We also extract sentinel lines emitted by the model to drive interactive
// elements that aren't text:
//
//   [[chips: A | B | C]]   → chip-row rendered below the bubble
//
// The sentinels are stripped from the displayed text. If the sentinel is
// malformed the line falls through to normal rendering so nothing crashes.

export interface ChipGroup {
  /**
   * - "chips"  → single-select; tap sends the label immediately
   * - "picks"  → multi-select; tap toggles the label into the composer so
   *             the prospect can mix selections with free text before sending
   * - "date"   → inline date picker; selecting a date sends it as a human-
   *             formatted string ("June 15, 2026") via the same handleSend
   *             path a chip click takes. No labels — the labels array is
   *             empty for date groups.
   */
  kind: "chips" | "picks" | "date" | "time";
  labels: string[];
}

export interface ParsedBubble {
  text: string;
  groups: ChipGroup[];
}

// `gim` flags: multi-line so `^` / `$` match per-line; global so we can find
// every sentinel in the reply. Non-greedy body so two sentinels on adjacent
// lines don't collapse into one.
const SENTINEL = /^\s*\[\[\s*(chips|picks)\s*:\s*([^\]]+?)\s*\]\]\s*$/gim;
// Date picker is a payload-less sentinel — just `[[date]]` on its own line.
const DATE_SENTINEL = /^\s*\[\[\s*date\s*\]\]\s*$/gim;
// Time picker — just `[[time]]` on its own line.
const TIME_SENTINEL = /^\s*\[\[\s*time\s*\]\]\s*$/gim;
// Defensive cleanup for the `[[break]]` pacing markers. The server strips
// these from the live stream and emits a `message_break` event instead, but
// a malformed marker (extra whitespace, missing newline) might slip through —
// render-time removal keeps the bubble clean regardless.
const BREAK_MARKER = /\[\[\s*break\s*\]\]\s*/gi;

export function parseAssistantText(raw: string): ParsedBubble {
  const groups: ChipGroup[] = [];
  // Reset lastIndex — global regexes share state across calls.
  SENTINEL.lastIndex = 0;
  for (const m of raw.matchAll(SENTINEL)) {
    const kind = (m[1] ?? "").toLowerCase() as ChipGroup["kind"];
    const labels = (m[2] ?? "")
      .split("|")
      .map((c) => c.trim())
      .filter((c) => c.length > 0 && c.length <= 60)
      .slice(0, 6);
    if (labels.length >= 2) groups.push({ kind, labels });
  }
  // Date sentinel — payload-less, just push a group with empty labels.
  DATE_SENTINEL.lastIndex = 0;
  if (DATE_SENTINEL.test(raw)) {
    groups.push({ kind: "date", labels: [] });
  }
  // Time sentinel — payload-less.
  TIME_SENTINEL.lastIndex = 0;
  if (TIME_SENTINEL.test(raw)) {
    groups.push({ kind: "time", labels: [] });
  }
  // Strip sentinels + stray break markers, then collapse extra newlines.
  const text = raw
    .replace(SENTINEL, "")
    .replace(DATE_SENTINEL, "")
    .replace(TIME_SENTINEL, "")
    .replace(BREAK_MARKER, "")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\s+$/, "");
  return { text, groups };
}

/**
 * Render a string with the supported inline markdown into a Preact node.
 * Returns a fragment of nodes — drop it into any JSX container.
 */
export function renderMarkdown(text: string): ComponentChildren {
  // Split by line first so we can intersperse <br/>s without nested complexity.
  const lines = text.split("\n");
  const out: ComponentChildren[] = [];
  for (let i = 0; i < lines.length; i++) {
    out.push(renderInline(lines[i] ?? ""));
    if (i < lines.length - 1) out.push(<br key={`br-${i}`} />);
  }
  return out;
}

// Inline-token grammar — non-nested, single-pass. Order matters: bold (**)
// before italic (*) so the longer marker wins. Code spans are independent.
function renderInline(line: string): ComponentChildren {
  if (!line) return "";
  const nodes: ComponentChildren[] = [];
  let i = 0;
  let buf = "";

  const flushBuf = () => {
    if (buf) {
      nodes.push(buf);
      buf = "";
    }
  };

  while (i < line.length) {
    const ch = line[i];
    const next = line[i + 1];

    // **bold**
    if (ch === "*" && next === "*") {
      const end = line.indexOf("**", i + 2);
      if (end !== -1) {
        flushBuf();
        const inner = line.slice(i + 2, end);
        nodes.push(<strong key={`b-${i}`}>{renderInline(inner)}</strong>);
        i = end + 2;
        continue;
      }
    }

    // *italic* — must NOT be a leftover from broken **; we already returned above.
    if (ch === "*") {
      const end = line.indexOf("*", i + 1);
      if (end !== -1 && line[end + 1] !== "*") {
        flushBuf();
        const inner = line.slice(i + 1, end);
        nodes.push(<em key={`i-${i}`}>{renderInline(inner)}</em>);
        i = end + 1;
        continue;
      }
    }

    // `code`
    if (ch === "`") {
      const end = line.indexOf("`", i + 1);
      if (end !== -1) {
        flushBuf();
        nodes.push(
          <code key={`c-${i}`} className="ccb-bx-code">
            {line.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }

    buf += ch;
    i++;
  }
  flushBuf();
  return nodes as VNode[];
}
